import { addMinutes, Duration, formatDuration, intervalToDuration } from 'date-fns';
import * as dateFnsLocales from 'date-fns/locale';
import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';

import { dateFnsLocaleFromLanguage } from '~/common/i18n';
import { I18nNamespace } from '~/common/types';

export const useDurationFormat = (format?: (keyof Duration)[]): ((minutes: number) => string) => {
  const { t, i18n } = useTranslation(I18nNamespace.common, { keyPrefix: 'Duration' });
  const dateFnsLocal = dateFnsLocaleFromLanguage[i18n.language] || dateFnsLocales.de;

  const formatFn = useCallback(
    (minutes: number): string => {
      if (minutes <= 0) return t('minutes', { count: 0 });

      const startDate = new Date(0);
      const endDate = addMinutes(startDate, minutes < 1 ? 1 : minutes);

      // Get the structured duration object
      const duration = intervalToDuration({ start: startDate, end: endDate });

      // Use formatDuration to get a human-readable format
      return formatDuration(duration, {
        locale: dateFnsLocal,
        format: format ?? ['years', 'months', 'weeks', 'days', 'hours', 'minutes'],
      });
    },
    [t, dateFnsLocal, format],
  );

  return formatFn;
};
